import { useInfiniteQuery } from "@tanstack/react-query";
import { toastServerError } from "helpers";
import { ApiError, ApiServiceError } from "libs/api";
import { useMemo } from "react";

import { SUPPORT_QUERY_KEY } from "../../constants/enums";
import { InAppConversationsListResponse } from "../../support.types";
import { requestGetConversations } from "./getConversations.request";
import { GetConversationsParams } from "./getConversations.types";

const DEFAULT_TAKE = 30;

type Filters = Omit<GetConversationsParams, "page">;

const useGetConversations = (filters: Filters = {}) => {
  const take = filters.take ?? DEFAULT_TAKE;

  const query = useInfiniteQuery<
    InAppConversationsListResponse,
    ApiServiceError<ApiError>
  >({
    queryKey: [SUPPORT_QUERY_KEY, "list", { ...filters, take }],
    queryFn: async ({ pageParam = 1 }) =>
      await requestGetConversations({ ...filters, take, page: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined,
    onError: toastServerError
  });

  const conversations = useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data]
  );

  return {
    conversations,
    count: query.data?.pages[0]?.meta.itemCount,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching
  };
};

export default useGetConversations;
